import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { logoutRequested, updateUser } from './authSlice';
import api from '../../api';

//TODO: thunk for updating reservation. need to upate schema

export const updateCurrentUser = createAsyncThunk(
  'myself/updateCurrentUser',
  async (userInfo, { rejectWithValue, dispatch }) => {
    const {
      firstName,
      lastName,
      password,
      oldPassword,
      address,
      city,
      zip,
      phoneNumber,
    } = userInfo;
    try {
      const res = await api.put(`/myself/`, {
        firstName,
        lastName,
        oldPassword,
        password,
        address,
        city,
        phoneNumber,
        zip,
      });
      dispatch(updateUser(res.data.data.user));
      toast.success('Success!');
      return res.data.data.user;
    } catch (error) {
      let errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.errors?.[0]?.message ||
        error.message ||
        'An error occurred while trying to update your password.';

      if (errorMessage === '"oldPassword" is not allowed to be empty') {
        errorMessage = 'Please enter your current password.';
      } else if (errorMessage === '"password" is not allowed to be empty') {
        errorMessage = 'Please enter a new password.';
      }
      toast.warn(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

/**Get the current user's reservations */
export const getMyReservations = createAsyncThunk(
  'myself/getMyReservations',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get(`/myself/reservations`);
      return res.data.data.reservations;
    } catch (error) {
      toast.warn(error.errors.message);
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to change user info.';
      return rejectWithValue(errorMessage);
    }
  },
);

/**Make a reservation for the current user */
export const makeReservation = createAsyncThunk(
  'myself/makeReservation',
  async (reservation, { rejectWithValue }) => {
    try {
      const res = await api.post(`/myself/reservations`, reservation);
      toast.success('Success!');
      return res.data.data;
    } catch (error) {
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to make a reservation.';
      toast.warn(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

/**Update a reservation for the current user */
export const updateMyReservation = createAsyncThunk(
  'myself/updateMyReservation',
  async (reservation, { rejectWithValue }) => {
    try {
      const resId = reservation._id;
      const res = await api.put(`/myself/reservations/${resId}`, reservation);
      toast.success('Success!');
      return res.data;
    } catch (error) {
      toast.warn(error.errors.message);
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to change reservation info.';
      return rejectWithValue(errorMessage);
    }
  },
);

/**Delete a reservation for the current user */
export const deleteMyReservation = createAsyncThunk(
  'myself/deleteMyReservation',
  async (reservationId, { rejectWithValue }) => {
    try {
      await api.delete(`/myself/reservations/${reservationId}`);
      toast.success('Success!');
      return reservationId;
    } catch (error) {
      toast.warn(error.errors.message);
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to delete a reservation.';
      return rejectWithValue(errorMessage);
    }
  },
);

const initialState = {
  reservations: [],
  loading: false,
  error: null,
};

const myselfSlice = createSlice({
  name: 'myself',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.loading = false;
      })
      .addCase(getMyReservations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMyReservations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getMyReservations.fulfilled, (state, action) => {
        state.loading = false;
        state.reservations = action.payload;
      })
      .addCase(makeReservation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(makeReservation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(makeReservation.fulfilled, (state, action) => {
        state.loading = false;
        state.reservations = [...state.reservations, action.payload];
      })
      .addCase(deleteMyReservation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMyReservation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteMyReservation.fulfilled, (state, action) => {
        state.loading = false;
        state.reservations = state.reservations.filter(
          (reservation) => reservation._id !== action.payload,
        );
      })
      .addCase(updateMyReservation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMyReservation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateMyReservation.fulfilled, (state, action) => {
        state.loading = false;
        state.reservations = state.reservations.map((reservation) =>
          reservation._id === action.payload._id ? action.payload : reservation,
        );
      })
      .addCase(logoutRequested, (state) => {
        state.reservations = [];
        state.loading = false;
      });
  },
});

export const { clearError } = myselfSlice.actions;
export default myselfSlice.reducer;
